/**
 * Course Structure Data
 * Defines the complete A-Z Heat Pump Course table of contents
 */

import {
  ApartmentOutlined,
  BranchesOutlined,
  BuildOutlined,
  ExperimentOutlined,
  FileSearchOutlined,
  HomeOutlined,
  ThunderboltOutlined,
  ToolOutlined,
} from '@ant-design/icons';

const curriculumRootPath = '/training-curriculum';

export const TRAINING_CURRICULUM_TYPES = {
  INSTALLER: 'installer',
  ENERGY_CONSULTANT: 'energyConsultant',
  CONSUMER: 'consumer',
};

export const trainingCurriculumTypeOrder = [
  TRAINING_CURRICULUM_TYPES.INSTALLER,
  TRAINING_CURRICULUM_TYPES.ENERGY_CONSULTANT,
  TRAINING_CURRICULUM_TYPES.CONSUMER,
];

export const installerCourseStructure = [
  {
    id: 'cold-room',
    nodeKey: '1',
    number: '1',
    title: 'Cold Room',
    icon: <ExperimentOutlined />,
    description: 'Understanding thermal dynamics in unheated spaces and heat loss mechanisms.',
    subsections: [
      { id: '1.1', nodeKey: '1.1', title: 'Heat Loss', link: `${curriculumRootPath}/heat-loss`, description: 'Fundamentals of thermal energy dissipation through building envelope.' },
      { id: '1.2', nodeKey: '1.2', title: 'Building U-Value', link: `${curriculumRootPath}/u-value`, description: 'Thermal transmittance coefficient and its calculation methods.' },
      { id: '1.3', nodeKey: '1.3', title: 'Ventilation', link: `${curriculumRootPath}/ventilation`, description: 'Air exchange rates and their impact on thermal balance.' },
      { id: '1.4', nodeKey: '1.4', title: 'Neighboring Rooms Heating Input', link: `${curriculumRootPath}/neighboring-rooms`, description: 'Heat transfer between adjacent spaces and its quantification.' }
    ]
  },
  {
    id: 'heat-input',
    nodeKey: '2',
    number: '2',
    title: 'Heat Input',
    icon: <ApartmentOutlined />,
    description: 'Comprehensive analysis of heat generation, distribution, and system performance optimization.',
    subsections: [
      { id: '2.1', nodeKey: '2.1', title: 'Heat Generation and Heat Transfer', link: `${curriculumRootPath}/heat-generation`, description: 'Principles of thermal energy production and conduction mechanisms.' },
      { id: '2.2', nodeKey: '2.2', title: 'Role of Supply Temperature', link: `${curriculumRootPath}/supply-temperature`, description: 'Impact of flow temperature on system efficiency and comfort.' },
      { id: '2.3', nodeKey: '2.3', title: 'Influence of System Temperatures', link: `${curriculumRootPath}/system-temperatures`, description: 'Delta-T analysis and temperature differential optimization.' },
      { id: '2.4', nodeKey: '2.4', title: 'Coefficient of Performance (COP)', link: `${curriculumRootPath}/cop`, description: 'Efficiency metric definition, calculation, and optimization strategies.' },
      { id: '2.5', nodeKey: '2.5', title: 'Changes in Building U-Value', link: `${curriculumRootPath}/u-value-changes`, description: 'Effects of insulation improvements and building modifications.' },
      { id: '2.6', nodeKey: '2.6', title: 'Radiator Transfer Coefficient', link: `${curriculumRootPath}/radiator-coefficient`, description: 'Heat transfer parameters and their influence on output.' },
      { id: '2.7', nodeKey: '2.7', title: 'Radiator Dimensioning', link: `${curriculumRootPath}/radiator-dimensioning`, description: 'Sizing methodology and selection criteria for heating elements.' },
      { id: '2.8', nodeKey: '2.8', title: 'Pipes and Valves', link: `${curriculumRootPath}/pipes-valves`, description: 'Hydraulic components, pressure drops, and flow regulation.' },
      { id: '2.9', nodeKey: '2.9', title: 'Pump Pressure', link: `${curriculumRootPath}/pump-pressure`, description: 'Head calculation, pump curves, and system integration.' }
    ]
  },
  {
    id: 'two-room-scenarios',
    nodeKey: '3',
    number: '3',
    title: 'Two Room Scenarios',
    icon: <ApartmentOutlined />,
    description: 'Practical multi-zone analysis and balancing techniques for interconnected spaces.',
    subsections: [
      { id: '3.1', nodeKey: '3.1', title: 'Symmetric Case', link: `${curriculumRootPath}/symmetric-case`, description: 'Baseline analysis with identical room configurations.' },
      { id: '3.2', nodeKey: '3.2', title: 'Changing Pre-set Valve Opening', link: `${curriculumRootPath}/valve-opening`, description: 'Flow regulation effects and balancing adjustments.' },
      { id: '3.3', nodeKey: '3.3', title: 'Changing Radiator Size', link: `${curriculumRootPath}/radiator-size`, description: 'Impact of heating surface modifications on system balance.' },
      { id: '3.4', nodeKey: '3.4', title: 'Low / High Pressure Setting', link: `${curriculumRootPath}/pressure-settings`, description: 'System pressure optimization and performance implications.' }
    ]
  },
  {
    id: 'case-studies',
    nodeKey: '4',
    number: '4',
    title: 'Case Studies',
    icon: <FileSearchOutlined />,
    description: 'Real-world applications, troubleshooting scenarios, and practical solutions.',
    subsections: [
      { id: '4.1', nodeKey: '4.1', title: 'Historic Buildings: Old vs. Renovated', link: `${curriculumRootPath}/historic-buildings`, description: 'Comparative analysis of heritage structures before and after retrofit.' },
      { id: '4.2', nodeKey: '4.2', title: 'System Noise', link: `${curriculumRootPath}/system-noise`, description: 'Causes, diagnostic procedures, and maintenance solutions.' },
      { id: '4.3', nodeKey: '4.3', title: 'Low COP with Adequate Heating', link: `${curriculumRootPath}/low-cop-case`, description: 'Investigating efficiency issues when comfort targets are met.' },
      { id: '4.4', nodeKey: '4.4', title: 'Excessive Supply Temperature', link: `${curriculumRootPath}/high-supply-temp`, description: 'Diagnosing and correcting oversized temperature settings.' }
    ]
  }
];

export const energyConsultantCourseStructure = [
  {
    id: 'building-heat-demand',
    nodeKey: '1',
    number: '1',
    title: 'Building Heat Demand Analysis',
    icon: <ExperimentOutlined />,
    description: 'Understanding heat losses, heat demand calculations, and building thermal performance.',
    subsections: [
      {
        id: '1.1',
        nodeKey: '1.1',
        title: 'Standard Heat Load Calculation',
        link: `${curriculumRootPath}/standard-heat-load`,
        description: 'Calculate design heat demand using temperature difference and heat loss coefficients.'
      },
      {
        id: '1.2',
        nodeKey: '1.2',
        title: 'Transmission Losses',
        link: `${curriculumRootPath}/transmission-losses`,
        description: 'Evaluate heat loss through walls, roofs, floors, windows, and doors.'
      },
      {
        id: '1.3',
        nodeKey: '1.3',
        title: 'Ventilation Losses',
        link: `${curriculumRootPath}/ventilation-losses`,
        description: 'Assess heat loss caused by air exchange and air change rates.'
      },
      {
        id: '1.4',
        nodeKey: '1.4',
        title: 'Total Building Heat Demand',
        link: `${curriculumRootPath}/total-building-heat-demand`,
        description: 'Combine room-level losses into whole-building heat demand calculations.'
      }
    ]
  },
  {
    id: 'building-retrofit-analysis',
    nodeKey: '2',
    number: '2',
    title: 'Building Retrofit Analysis',
    icon: <ApartmentOutlined />,
    description: 'Evaluating building improvements and their impact on energy demand.',
    subsections: [
      {
        id: '2.1',
        nodeKey: '2.1',
        title: 'Building Envelope Assessment',
        link: `${curriculumRootPath}/building-envelope`,
        description: 'Analyze materials, construction age, insulation levels, and thermal performance.'
      },
      {
        id: '2.2',
        nodeKey: '2.2',
        title: 'U-Values and Surface Areas',
        link: `${curriculumRootPath}/u-values`,
        description: 'Understand how thermal transmittance and area influence heat loss.'
      },
      {
        id: '2.3',
        nodeKey: '2.3',
        title: 'Insulation Improvement Strategies',
        link: `${curriculumRootPath}/insulation-improvements`,
        description: 'Compare retrofit measures and expected energy savings.'
      },
      {
        id: '2.4',
        nodeKey: '2.4',
        title: 'Airtightness and Ventilation',
        link: `${curriculumRootPath}/airtightness`,
        description: 'Evaluate air leakage reduction and ventilation optimization.'
      }
    ]
  },
  {
    id: 'heat-pump-sizing',
    nodeKey: '3',
    number: '3',
    title: 'Heat Pump System Sizing',
    icon: <ToolOutlined />,
    description: 'Translating building heat demand into properly sized heat pump systems.',
    subsections: [
      {
        id: '3.1',
        nodeKey: '3.1',
        title: 'Design Outdoor Temperature',
        link: `${curriculumRootPath}/design-temperature`,
        description: 'Apply normative outdoor temperatures for system sizing.'
      },
      {
        id: '3.2',
        nodeKey: '3.2',
        title: 'Heating Capacity Determination',
        link: `${curriculumRootPath}/heating-capacity`,
        description: 'Calculate required heat pump capacity based on peak demand.'
      },
      {
        id: '3.3',
        nodeKey: '3.3',
        title: 'Sizing Risks and Trade-Offs',
        link: `${curriculumRootPath}/sizing-risks`,
        description: 'Understand oversizing, undersizing, and design margins.'
      },
      {
        id: '3.4',
        nodeKey: '3.4',
        title: 'System Design Evaluation',
        link: `${curriculumRootPath}/system-design`,
        description: 'Compare heat pump system configurations and design choices.'
      }
    ]
  },
  {
    id: 'heat-emission-systems',
    nodeKey: '4',
    number: '4',
    title: 'Heat Emission Systems',
    icon: <ApartmentOutlined />,
    description: 'Assessing emitters, temperatures, and heat pump compatibility.',
    subsections: [
      {
        id: '4.1',
        nodeKey: '4.1',
        title: 'Room Heat Emission Analysis',
        link: `${curriculumRootPath}/heat-emission-analysis`,
        description: 'Determine room-by-room heat emission requirements.'
      },
      {
        id: '4.2',
        nodeKey: '4.2',
        title: 'Emitter Performance',
        link: `${curriculumRootPath}/emitter-performance`,
        description: 'Compare radiators and other emitter technologies.'
      },
      {
        id: '4.3',
        nodeKey: '4.3',
        title: 'System Temperatures',
        link: `${curriculumRootPath}/system-temperatures`,
        description: 'Evaluate supply and return temperatures and their impact on efficiency.'
      },
      {
        id: '4.4',
        nodeKey: '4.4',
        title: 'Emitter Compatibility',
        link: `${curriculumRootPath}/emitter-compatibility`,
        description: 'Assess suitability of existing emitters for heat pump operation.'
      }
    ]
  },
  {
    id: 'hydraulic-performance',
    nodeKey: '5',
    number: '5',
    title: 'Hydraulic Systems and Optimization',
    icon: <BranchesOutlined />,
    description: 'Analyzing hydraulic performance and system balancing.',
    subsections: [
      {
        id: '5.1',
        nodeKey: '5.1',
        title: 'Hydraulic Fundamentals',
        link: `${curriculumRootPath}/hydraulic-fundamentals`,
        description: 'Understand flow, pressure, and resistance in heating circuits.'
      },
      {
        id: '5.2',
        nodeKey: '5.2',
        title: 'Hydraulic Balancing',
        link: `${curriculumRootPath}/hydraulic-balancing`,
        description: 'Optimize heat distribution through valve and pump adjustments.'
      },
      {
        id: '5.3',
        nodeKey: '5.3',
        title: 'Performance Evaluation',
        link: `${curriculumRootPath}/hydraulic-performance-evaluation`,
        description: 'Assess post-balancing performance and identify inefficiencies.'
      }
    ]
  }
];

export const consumerCourseStructure = [
  {
    id: 'understanding-home-energy',
    nodeKey: '1',
    number: '1',
    title: 'Understanding Home Energy Demand',
    icon: <HomeOutlined />,
    description: 'Learn why homes require heating and what drives energy consumption.',
    subsections: [
      {
        id: '1.1',
        nodeKey: '1.1',
        title: 'Why Homes Lose Heat',
        link: `${curriculumRootPath}/why-homes-lose-heat`,
        description: 'Understand heat escaping through walls, roofs, windows, and ventilation.'
      },
      {
        id: '1.2',
        nodeKey: '1.2',
        title: 'Indoor vs Outdoor Temperature',
        link: `${curriculumRootPath}/temperature-difference`,
        description: 'Learn how temperature differences affect heating demand.'
      },
      {
        id: '1.3',
        nodeKey: '1.3',
        title: 'Building Age and Energy Performance',
        link: `${curriculumRootPath}/building-age`,
        description: 'Understand why older buildings often consume more energy.'
      }
    ]
  },
  {
    id: 'improving-home-efficiency',
    nodeKey: '2',
    number: '2',
    title: 'Improving Home Efficiency',
    icon: <BuildOutlined />,
    description: 'Discover the most effective building upgrades for reducing energy use.',
    subsections: [
      {
        id: '2.1',
        nodeKey: '2.1',
        title: 'Insulation Basics',
        link: `${curriculumRootPath}/insulation-basics`,
        description: 'Learn how insulation reduces heat loss and lowers energy bills.'
      },
      {
        id: '2.2',
        nodeKey: '2.2',
        title: 'Windows, Walls and Roofs',
        link: `${curriculumRootPath}/building-elements`,
        description: 'Compare the impact of upgrading different parts of a building.'
      },
      {
        id: '2.3',
        nodeKey: '2.3',
        title: 'Airtightness and Ventilation',
        link: `${curriculumRootPath}/airtightness-ventilation`,
        description: 'Balance energy savings, comfort, and indoor air quality.'
      }
    ]
  },
  {
    id: 'heat-pumps-explained',
    nodeKey: '3',
    number: '3',
    title: 'Heat Pumps Explained',
    icon: <ThunderboltOutlined />,
    description: 'Understand how heat pumps work and how they are sized.',
    subsections: [
      {
        id: '3.1',
        nodeKey: '3.1',
        title: 'How Heat Pumps Produce Heat',
        link: `${curriculumRootPath}/heat-pump-basics`,
        description: 'Learn the fundamentals of heat pump technology.'
      },
      {
        id: '3.2',
        nodeKey: '3.2',
        title: 'Choosing the Right Size',
        link: `${curriculumRootPath}/heat-pump-sizing-consumer`,
        description: 'Understand why correct sizing is critical for comfort and efficiency.'
      },
      {
        id: '3.3',
        nodeKey: '3.3',
        title: 'Efficiency and Running Costs',
        link: `${curriculumRootPath}/efficiency-costs`,
        description: 'Learn how efficiency influences electricity consumption and costs.'
      }
    ]
  },
  {
    id: 'heating-system-performance',
    nodeKey: '4',
    number: '4',
    title: 'Heating System Performance',
    icon: <ApartmentOutlined />,
    description: 'Understand how radiators, temperatures, and controls affect comfort.',
    subsections: [
      {
        id: '4.1',
        nodeKey: '4.1',
        title: 'Radiators and Heat Delivery',
        link: `${curriculumRootPath}/radiators-and-heat`,
        description: 'Learn how heating systems deliver heat into rooms.'
      },
      {
        id: '4.2',
        nodeKey: '4.2',
        title: 'Low Temperature Heating',
        link: `${curriculumRootPath}/low-temperature-heating`,
        description: 'Understand why lower system temperatures improve efficiency.'
      },
      {
        id: '4.3',
        nodeKey: '4.3',
        title: 'Comfort and Energy Use',
        link: `${curriculumRootPath}/comfort-energy-use`,
        description: 'Explore the relationship between comfort settings and consumption.'
      }
    ]
  },
  {
    id: 'buying-and-operating',
    nodeKey: '5',
    number: '5',
    title: 'Buying and Operating a Heat Pump',
    icon: <FileSearchOutlined />,
    description: 'Prepare for heat pump investment and long-term operation.',
    subsections: [
      {
        id: '5.1',
        nodeKey: '5.1',
        title: 'Evaluating Retrofit Options',
        link: `${curriculumRootPath}/retrofit-options`,
        description: 'Identify the most valuable improvements before installing a heat pump.'
      },
      {
        id: '5.2',
        nodeKey: '5.2',
        title: 'Questions to Ask Installers',
        link: `${curriculumRootPath}/questions-for-installers`,
        description: 'Understand the key information needed to compare proposals.'
      },
      {
        id: '5.3',
        nodeKey: '5.3',
        title: 'Understanding System Performance',
        link: `${curriculumRootPath}/understanding-performance`,
        description: 'Interpret efficiency, comfort, and energy consumption after installation.'
      }
    ]
  }
];

export const trainingCurriculumStructures = {
  [TRAINING_CURRICULUM_TYPES.INSTALLER]: installerCourseStructure,
  [TRAINING_CURRICULUM_TYPES.ENERGY_CONSULTANT]: energyConsultantCourseStructure,
  [TRAINING_CURRICULUM_TYPES.CONSUMER]: consumerCourseStructure,
};

export const courseStructure = installerCourseStructure;

export const getCourseStructureByType = (curriculumType = TRAINING_CURRICULUM_TYPES.INSTALLER) => {
  return trainingCurriculumStructures[curriculumType] || installerCourseStructure;
};

export const flattenCourseStructure = (sections = installerCourseStructure) => {
  return sections.flatMap((section) => [
    {
      nodeKey: section.nodeKey,
      nodeType: 'section',
      id: section.id,
      title: section.title,
      description: section.description,
      number: section.number,
    },
    ...section.subsections.map((subsection) => ({
      nodeKey: subsection.nodeKey,
      nodeType: 'topic',
      id: subsection.id,
      parentNodeKey: section.nodeKey,
      title: subsection.title,
      description: subsection.description,
      number: subsection.id,
    })),
  ]);
};

export const flattenCourseStructureByType = (curriculumType = TRAINING_CURRICULUM_TYPES.INSTALLER) => {
  return flattenCourseStructure(getCourseStructureByType(curriculumType));
};

export const getCurriculumNodeByKey = (nodeKey, curriculumType = TRAINING_CURRICULUM_TYPES.INSTALLER) => {
  return flattenCourseStructureByType(curriculumType).find((node) => node.nodeKey === nodeKey) || null;
};
